import { useNavigate } from '@tanstack/react-router'
import { Button } from '@workspace/ui/components/button'
import { ErrorBanner } from '@workspace/ui/components/error-banner'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { useState } from 'react'
import {
  usePitchQuery,
  useStartPitchMutation,
  useStartupQuery,
  useUpdatePitchMutation,
} from '@/hooks/api'
import { usePitchCoverImage } from '@/hooks/use-pitch-cover-image'
import type { MediaSettings } from '../types/pitch.types'
import { LiveKitRoom } from './livekit-room'
import { PitchPreparationProgress } from './pitch-preparation-progress'
import { PitchSessionDetail } from './pitch-session-detail'
import { PreJoinScreen } from './pre-join-screen'

const POLL_INTERVAL_PENDING = 3000
const POLL_INTERVAL_FAST = 5000
const POLL_INTERVAL_SLOW = 30000

interface FounderPitchDetailProps {
  pitchId: string
}

export function FounderPitchDetail({ pitchId }: FounderPitchDetailProps) {
  const navigate = useNavigate()
  const { data: startupResponse } = useStartupQuery()
  const startPitch = useStartPitchMutation()
  const [isJoining, setIsJoining] = useState(false)
  const [joinError, setJoinError] = useState<string | null>(null)
  const [mediaSettings, setMediaSettings] = useState<MediaSettings>({
    audioEnabled: true,
    videoEnabled: true,
  })
  const coverImage = usePitchCoverImage(pitchId)
  const updatePitch = useUpdatePitchMutation(pitchId)

  const {
    data: pitchResponse,
    isLoading,
    error,
  } = usePitchQuery(pitchId, {
    refetchInterval: (query) => {
      const data = query.state.data?.data
      const status = data?.status

      if (status === 'PENDING') {
        return POLL_INTERVAL_PENDING
      }

      if (status === 'COMPLETED') {
        const hasVideo = !!data?.videoKey
        const hasEvaluations = data?.evaluations && data.evaluations.length > 0
        if (!hasVideo || !hasEvaluations) {
          return POLL_INTERVAL_FAST
        }
        return POLL_INTERVAL_SLOW
      }

      return false
    },
  })
  const pitchSession = pitchResponse?.data

  const handleTogglePublish = () => {
    updatePitch.mutate({
      path: { pitchId },
      body: { isPublished: !pitchSession?.isPublished },
    })
  }

  const handleJoinSession = async (settings: MediaSettings) => {
    if (!pitchSession?.id) return
    setIsJoining(true)
    setJoinError(null)
    setMediaSettings(settings)
    try {
      await startPitch.mutateAsync({
        path: { pitchId: pitchSession.id },
      })
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'Failed to join session')
      setIsJoining(false)
    }
  }

  const handleBack = () => {
    navigate({ to: '/pitches' })
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !pitchSession) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <ErrorBanner message={error?.message || 'Failed to load pitch session'} />
      </div>
    )
  }

  // PENDING - show interactive preparation progress
  if (pitchSession.status === 'PENDING') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4">
        <PitchPreparationProgress
          createdAt={pitchSession.createdAt}
          preparationStep={pitchSession.preparationStep}
          onBack={handleBack}
        />
      </div>
    )
  }

  // ACTIVE - pre-join screen with audio/video setup (fullscreen like conference room)
  if (pitchSession.status === 'ACTIVE') {
    return (
      <div className="fixed inset-0 z-50 bg-background">
        <PreJoinScreen
          onJoin={handleJoinSession}
          onCancel={handleBack}
          isLoading={isJoining}
          error={joinError}
        />
      </div>
    )
  }

  // IN_PROGRESS - fullscreen LiveKit room
  if (pitchSession.status === 'IN_PROGRESS') {
    return <LiveKitRoom pitchSession={pitchSession} mediaSettings={mediaSettings} />
  }

  // PENDING, COMPLETED, FAILED, INCOMPLETE - show status
  const hasVideo = !!pitchSession.videoKey
  const evaluation = pitchSession.evaluations?.[0] ?? null
  const hasEvaluations = evaluation && evaluation.individualMetricEvaluations.length > 0
  const isDataReady = pitchSession.status === 'COMPLETED' && hasVideo && hasEvaluations

  return (
    <div className={isDataReady ? 'py-8' : 'flex flex-1 items-center justify-center'}>
      <div className={isDataReady ? undefined : 'mx-4 w-full max-w-md'}>
        <PitchSessionDetail
          status={pitchSession.status}
          preparationStep={pitchSession.preparationStep}
          pitchId={pitchId}
          videoKey={pitchSession.videoKey}
          videoCoverImageUrl={pitchSession.videoCoverImageUrl}
          pitchDeckId={pitchSession.pitchDeckId}
          evaluation={evaluation}
          createdAt={pitchSession.createdAt}
          finishedAt={pitchSession.finishedAt}
          onBack={handleBack}
          onCoverImageUpload={isDataReady ? coverImage.triggerUpload : undefined}
          onCoverImageRemove={isDataReady ? coverImage.removeCoverImage : undefined}
        />
        {isDataReady && (
          <>
            <input
              ref={coverImage.fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={coverImage.handleFileChange}
            />
            <div className="mt-6 flex items-center justify-between">
              <div>
                {isDataReady && startupResponse?.data?.id === pitchSession.startupId && (
                  <Button
                    variant={pitchSession.isPublished ? 'outline' : 'default'}
                    onClick={handleTogglePublish}
                    disabled={updatePitch.isPending}
                  >
                    {pitchSession.isPublished ? 'Unpublish' : 'Publish to Profile'}
                  </Button>
                )}
              </div>
              <Button onClick={handleBack}>Go Back</Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
